import _ from 'underscore';

import {
  ABLVerificationStatus,
  AccountVerificationStatus as AccountVerificationStatusEnum,
  AccountToAblVerificationStatusRelation,
  AccountVerificationStatusIconMapping,
  AccountInactiveCauseCode,
  UnableTriggerAivReason,
  OrderedUnableTriggerAivReasons,
} from './constants';

export const getAccountVerificationStatusMappingForUI = ({ i18n, isInternal = false }) => _.chain(AccountVerificationStatusEnum)
  .invert()
  .mapObject((statusName, statusValue) => ({
    ...AccountVerificationStatusIconMapping[statusName],
    label: isInternal ? i18n.getString(`AccountVerificationStatus_${statusValue}_Internal`) : i18n.getString(`AccountVerificationStatus_${statusValue}`),
  }))
  .value();

export const getAccountVerificationStatusForUI = ({ i18n, accountVerificationStatus, isInternal = false }) =>
  getAccountVerificationStatusMappingForUI({ i18n, isInternal })[accountVerificationStatus] || ({ label: i18n.getString('Global_NotApplicableText') });

export const getABLVerificationStatusMappingForUI = ({ i18n }) => _.chain(ABLVerificationStatus)
  .invert()
  .mapObject((statusName, statusValue) => ({
    label: i18n.getString(`ABLVerificationStatus_${statusValue}`),
  }))
  .value();

export const getABLVerificationStatusForUI = ({ i18n, ablVerificationStatus }) =>
  getABLVerificationStatusMappingForUI({ i18n })[ablVerificationStatus] || ({ label: i18n.getString('Global_NotApplicableText') });

export const isAccountABLVerificationStatusMismatch = ({ accountVerificationStatus, ablVerificationStatus }) => {
  const ablVerificationStatusMatchList = AccountToAblVerificationStatusRelation[accountVerificationStatus];
  return _.values(ABLVerificationStatus).includes(ablVerificationStatus) && _.isArray(ablVerificationStatusMatchList) && !ablVerificationStatusMatchList.includes(ablVerificationStatus);
};

export const isAccountVerificationStatusExists = accountVerificationStatus => _.contains(_.values(AccountVerificationStatusEnum), accountVerificationStatus);

export const isAccountVerificationRequestSubmitted = accountVerificationStatus => isAccountVerificationStatusExists(accountVerificationStatus) && !_.contains([AccountVerificationStatusEnum.NoNeedVerified, AccountVerificationStatusEnum.NeedVerified], accountVerificationStatus);

export const isInPilot = ({ AccountVerificationStatus }) => isAccountVerificationStatusExists(AccountVerificationStatus) && AccountVerificationStatus !== AccountVerificationStatusEnum.NoNeedVerified;

export const isABLVerificationStatusExists = ablVerificationStatus => _.contains(_.values(ABLVerificationStatus), ablVerificationStatus);

export const findABLVerificationStatus = ({ addressList, businessName, countryCode }) => {
  const { ABLVerificationStatus: ablVerificationStatus } = _.findWhere(addressList, { BusinessName: businessName, Country: countryCode }) || {};
  return isABLVerificationStatusExists(ablVerificationStatus) ? ablVerificationStatus : ABLVerificationStatus.NotVerified;
};

export const isAivHold = ({ AccountInactiveReasonsV1 } = {}) =>
  !_.isEmpty(AccountInactiveReasonsV1) && _.contains(AccountInactiveReasonsV1.map(item => item.Causes[0]), AccountInactiveCauseCode.AccountVerificationHold);

export const isUnableTriggerAivReasonExists = unableTriggerAivReason => _.contains(_.values(UnableTriggerAivReason), unableTriggerAivReason);

export const findUnableTriggerAivReason = unableTriggerAivReasons =>
  _.find(OrderedUnableTriggerAivReasons, orderedReason => isUnableTriggerAivReasonExists(orderedReason) && _.contains(unableTriggerAivReasons, orderedReason));

export const isInitializeInProgress = unableTriggerAivReasons => _.contains(unableTriggerAivReasons, UnableTriggerAivReason.InitializeInProgress);

/**
 * Check if the user is unable to trigger email verification. It should align with MT logic.
 * 1. User is internal; 2. User is not super admin; 3. User's parent customer id is not cid in url.
 * @param {object} userContext get from userContext(UserContext)
 * @param {cid} cid Customer id in Url
 * @returns {bool} if the user is unable to retrigger email verification
 */
export const isUnableToRetriggerBusinessVerification = ({ userContext, cid }) => {
  const currentUser = _.get(userContext, 'CurrentUser');
  return !currentUser || currentUser.IsInternal || currentUser.ParentCustomerId !== cid || !currentUser.IsSuperAdmin;
};
